import React from "react";
import moment from "moment";
import _ from "lodash";
import { NavLink } from "react-router-dom";
import { AnalysisReportsAPI, NetworkAPI, UnitAPI } from "apis";
import DateRangePicker from "components/common/DateRangePicker";
import NetworksSelector from "components/ops-mgmt/account/NetworksSelector";
import YieldSetsMultiSelector from "components/ops-mgmt/account/YieldSetsMultiSelector";
import RequestTypesSelector from "components/ops-mgmt/account/RequestTypesSelector";
import LoadingUI from "components/common/LoadingUI";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import Checkbox from "components/common/Checkbox";
import PromptModal from "components/common/PromptModal";
import ScrollToTop from "components/common/ScrollToTop";
import SwitchTabs from "components/common/SwitchTabs";
import { getQueryStringToObject, setQueryString } from "helpers/QueryString";
import { MESSAGE } from "constants/Message";
import {
  DATAVIEW,
  REPORT,
  PROCESSED_REPORTS,
} from "./IntegrationVerificationHelper";
import SourceDataView from "./SourceDataView";
import ProcessedDataView from "./ProcessedDataView";

import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaExternalLinkAlt } from "react-icons/fa";

const reportPaths = {
  abTesting: "/integration-verification/ab-testing",
  codeSnippet: "/integration-verification/code-snippet",
};

const fixedWidthStyle = { width: "1280px", margin: "auto" };

const baseNavButton =
  "rounded px-4 py-1 font-semibold text-gray-700 bg-gray-200 hover:bg-gray-200 hover:text-gray-800";

const baseButton =
  "rounded px-4 py-1 font-semibold text-gray-700 bg-gray-300 border border-gray-400 hover:bg-gray-200 hover:text-gray-800";

const selectedButton =
  "rounded px-4 py-1 font-semibold text-blue-800 bg-blue-200";

const tableStyleMap = new Map()
  .set("date", { className: "w-32" })
  .set("ext_gam_unit_id", { className: "w-32" });

const DEFAULT_NUM_DAYS = 3;

const TABS = {
  EXT_GAM_UNIT_ID: "ext_gam_unit_id",
  YIELD_SET: "yield_set",
};

class IntegrationVerificationViewer extends React.Component {
  constructor(props) {
    super(props);

    const end = moment().subtract(1, "day");
    const start = end.clone().subtract(DEFAULT_NUM_DAYS, "day");

    this.state = {
      isLoading: false,
      errMsg: null,
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      selectedNetworkId: null,
      selectedNetwork: null,
      selectedDataview: DATAVIEW.PROCESSED,
      checkInputExtGamUnitIds: true,
      inputExtGamUnitIds: "",
      selectedExtGamUnitIds: [],
      selectedYieldSet: null,
      selectedYieldSetIds: null,
      ysExtGamUnitIds: [],
      selectedRequestTypes: [],
      isForceReload: false,
      tabList: _.values(TABS).map((t) => ({ value: t, label: _.startCase(t) })),
      selectedTab: TABS.EXT_GAM_UNIT_ID,

      data: null,
      builds: [],
      reports: null,
      selectedReport: null,
      updateTime: null,
      inputSearch: "",

      pageNum: 15,
      pageIdx: 0,

      tempParam: {
        report: null,
        networkId: null,
        startDate: null,
        endDate: null,
        extGamUnitIds: null,
        reload: false,
        requestType: null,
      },

      isDownloading: false,
      isPromptModalOpen: false,
      isSaving: false,
      promptMsg: [],
      promptHeader: "",
      promptMessage: "",
      promptAction: null,

      isShowAdvanced: false,
    };

    this.handleNetworksChanged = this.handleNetworksChanged.bind(this);
    this.handleDateRangeFilterChanged =
      this.handleDateRangeFilterChanged.bind(this);
    this.handleGetData = this.handleGetData.bind(this);
    this.handleReportChanged = this.handleReportChanged.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleExtGamUnitIdsChange = this.handleExtGamUnitIdsChange.bind(this);
    this.handleYieldSetChange = this.handleYieldSetChange.bind(this);
    this.handleSearchChanged = this.handleSearchChanged.bind(this);
    this.handleDownloadData = this.handleDownloadData.bind(this);
    this.handleDataViewChanged = this.handleDataViewChanged.bind(this);

    this.validateExtGamUnitIds = this.validateExtGamUnitIds.bind(this);
    this.debounceValidation = _.debounce(this.validateExtGamUnitIds, 300);
  }

  async componentDidMount() {
    const { networkId, startDate, endDate, extGamUnitIds, yieldSetIds } =
      getQueryStringToObject(this.props.location.search);

    const stateData = {};

    if (startDate) stateData.startDate = startDate;
    if (endDate) stateData.endDate = endDate;
    if (networkId) stateData.selectedNetworkId = _.toInteger(networkId);

    if (extGamUnitIds) {
      stateData.selectedTab = TABS.EXT_GAM_UNIT_ID;
      stateData.selectedExtGamUnitIds = extGamUnitIds.split(",");
      stateData.inputExtGamUnitIds = extGamUnitIds;
    } else if (yieldSetIds) {
      stateData.selectedTab = TABS.YIELD_SET;
      stateData.selectedYieldSetIds = yieldSetIds
        .split(",")
        .map((d) => _.toInteger(d));
    }

    this.setState(stateData);
  }

  componentWillUnmount() {
    this.debouncedValidation.cancel();
  }

  handleDateRangeFilterChanged({ startDate, endDate }) {
    setQueryString(this.props, {
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
    });

    this.setState({
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
    });
  }

  handleNetworksChanged(network) {
    const { networkId } = network;

    setQueryString(this.props, { networkId });

    this.setState((prevState) => {
      const stateData = {};
      if (prevState.selectedNetworkId !== networkId) {
        stateData.selectedExtGamUnitIds = [];
        stateData.inputExtGamUnitIds = "";
        stateData.selectedYieldSet = null;
        stateData.selectedYieldSetIds = null;
      }

      return {
        selectedNetworkId: networkId,
        selectedNetwork: network,
        ...stateData,
      };
    });
  }

  handleReportChanged(report) {
    this.setState({ selectedReport: report, pageIdx: 0 });
  }

  handleTabChange(tab) {
    this.setState({ selectedTab: tab });
  }

  handleYieldSetChange(list) {
    this.setState({
      ysExtGamUnitIds: [],
      selectedYieldSet: list.length > 0 ? list : null,
      selectedYieldSetIds:
        list.length > 0 ? list.map((d) => d.yieldSetId) : null,
      pageIdx: 0,
    });
  }

  handleExtGamUnitIdsChange(e) {
    this.setState({ inputExtGamUnitIds: e.target.value });
    this.debounceValidation(e.target.value);
  }

  handleSearchChanged(e) {
    this.setState({ inputSearch: e.target.value, pageIdx: 0 });
  }

  handleDataViewChanged(dv) {
    const { data } = this.state;
    const report = _.get(this.props, "match.params.report");

    const reportList =
      dv === REPORT.AB_TESTING
        ? _.keys(data)
        : _.values(PROCESSED_REPORTS[report]);

    if (!_.includes(reportList, this.state.selectedReport)) {
      this.setState({ selectedReport: reportList[0] });
    }
    this.setState({ selectedDataview: dv });
  }

  async handleGetData() {
    const {
      selectedNetworkId,
      startDate,
      endDate,
      isForceReload,
      selectedExtGamUnitIds,
      selectedRequestTypes,
      selectedTab,
      selectedYieldSetIds,
    } = this.state;

    const report = _.get(this.props, "match.params.report");
    const days = moment(endDate).diff(moment(startDate), "days");
    try {
      this.setState({
        isLoading: true,
        errMsg: null,
        data: null,
        updateTime: null,
      });

      if (days > 7) {
        throw new Error("Invalid date range: limit 7 days");
      }

      let extGamUnitIds = selectedExtGamUnitIds;
      if (selectedTab === TABS.YIELD_SET) {
        const { units } = await UnitAPI.searchUnits({
          networkId: selectedNetworkId,
          yieldSetIds: selectedYieldSetIds,
        });

        if (_.isNil(units) || units.length === 0) {
          throw new Error("No unit found for the selected yield set(s)");
        }

        extGamUnitIds = _.map(units, "extGamAdUnitId");
      }

      const params = {
        networkId: selectedNetworkId,
        startDate,
        endDate,
        forceReload: isForceReload,
        extGamUnitIds,
        overriddenRequestTypes: selectedRequestTypes,
      };

      setQueryString(
        this.props,
        selectedTab === TABS.EXT_GAM_UNIT_ID
          ? {
              extGamUnitIds:
                selectedExtGamUnitIds && selectedExtGamUnitIds.length > 0
                  ? selectedExtGamUnitIds
                  : null,
              yieldSetIds: null,
            }
          : {
              extGamUnitIds: null,
              yieldSetIds:
                selectedYieldSetIds && selectedYieldSetIds.length > 0
                  ? selectedYieldSetIds
                  : null,
            }
      );

      let result = null;
      let builds = [];

      if (report === REPORT.AB_TESTING) {
        // fetch ab testing data
        result = await AnalysisReportsAPI.getAbTestingVerificationReports(
          params
        );
      } else if (report === REPORT.CODE_SNIPPET) {
        // fetch code snippet data
        const [reportResult, buildResult] = await Promise.all([
          AnalysisReportsAPI.getCodeSnippetIntegrationVerificationReports(
            params
          ),
          NetworkAPI.getCodeSnippetBuilds({ networkId: selectedNetworkId }),
        ]);
        result = reportResult;
        builds = _.orderBy(
          buildResult.buildHistory || [],
          ["createdAt"],
          ["desc"]
        );
      }

      const { data_generated_time } = result;
      delete result.data_generated_time;

      const stateData = {};
      if (selectedTab === TABS.YIELD_SET) {
        stateData.ysExtGamUnitIds = extGamUnitIds;
      }

      this.setState({
        data: result,
        builds,
        pageIdx: 0,
        inputSearch: "",
        selectedReport: _.keys(result)[0],
        updateTime: data_generated_time,
        tempParam: {
          report,
          networkId: selectedNetworkId,
          startDate,
          endDate,
          extGamUnitIds: selectedExtGamUnitIds,
          reload: isForceReload,
          requestType: selectedRequestTypes,
          yieldSetIds: selectedYieldSetIds,
          paramType: selectedTab,
        },
        ...stateData,
      });
    } catch (err) {
      this.setState({ errMsg: err.message, data: null, updateTime: null });
    }

    this.setState({ isLoading: false });
  }

  async handleDownloadData() {
    const {
      selectedNetwork,
      selectedNetworkId,
      startDate,
      endDate,
      isForceReload,
      selectedExtGamUnitIds,
      selectedRequestTypes,
      selectedTab,
      selectedYieldSet,
      selectedYieldSetIds,
    } = this.state;
    const report = _.get(this.props, "match.params.report");
    const days = moment(endDate).diff(moment(startDate), "days");

    this.setState({
      isPromptModalOpen: true,
      isDownloading: true,
      promptMsg: [],
      promptHeader: `Download ${
        report === REPORT.AB_TESTING ? "A/B Testing" : "Code Snippet"
      } Report`,
      promptMessage: (
        <DownloadMessage
          networkId={selectedNetworkId}
          networkName={selectedNetwork ? selectedNetwork.name : ""}
          startDate={startDate}
          endDate={endDate}
          isForceReload={isForceReload}
          selectedTab={selectedTab}
          selectedYieldSet={selectedYieldSet}
          selectedRequestTypes={selectedRequestTypes}
          extGamUnitIds={
            selectedTab === TABS.EXT_GAM_UNIT_ID ? selectedExtGamUnitIds : null
          }
        />
      ),
    });

    let extGamUnitIds = selectedExtGamUnitIds;
    if (selectedTab === TABS.YIELD_SET) {
      const { units } = await UnitAPI.searchUnits({
        networkId: selectedNetworkId,
        yieldSetIds: selectedYieldSetIds,
      });

      if (_.isNil(units) || units.length === 0) {
        throw new Error("No unit found for the selected yield set(s)");
      }

      extGamUnitIds = _.map(units, "extGamAdUnitId");

      this.setState({
        promptMessage: (
          <DownloadMessage
            networkId={selectedNetworkId}
            networkName={selectedNetwork ? selectedNetwork.name : ""}
            startDate={startDate}
            endDate={endDate}
            isForceReload={isForceReload}
            selectedTab={selectedTab}
            selectedYieldSet={selectedYieldSet}
            selectedRequestTypes={selectedRequestTypes}
            extGamUnitIds={extGamUnitIds}
          />
        ),
      });
    }

    const params = {
      networkId: selectedNetworkId,
      startDate,
      endDate,
      forceReload: isForceReload,
      extGamUnitIds,
      returnDownloadLink: true,
      overriddenRequestTypes: selectedRequestTypes,
    };

    try {
      let url = null;

      if (days > 7) {
        throw new Error("Invalid date range: limit 7 days");
      }

      if (report === REPORT.AB_TESTING) {
        // fetch ab testing data
        url = await AnalysisReportsAPI.getAbTestingVerificationReports(params);
      } else if (report === REPORT.CODE_SNIPPET) {
        // fetch code snippet data
        url =
          await AnalysisReportsAPI.getCodeSnippetIntegrationVerificationReports(
            params
          );
      }

      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        this.setState({
          isDownloading: false,
          promptMsg: ["Download Successfully", MESSAGE.SUCCESS],
        });
      }
    } catch (err) {
      this.setState({
        isDownloading: false,
        promptMsg: [err.toString(), MESSAGE.ERROR],
      });
    }
  }

  validateExtGamUnitIds(value) {
    const regex = /^\d+(\s*,\s*\d+)*(\s*,\s*)?\s*$/;
    const check = value === "" ? true : regex.test(value);

    if (check) {
      this.setState({
        checkInputExtGamUnitIds: check,
        selectedExtGamUnitIds:
          value === ""
            ? []
            : value
                .split(",")
                .map((d) => _.trim(d))
                .filter((d) => d !== ""),
      });
    } else {
      this.setState({ checkInputExtGamUnitIds: check });
    }
  }

  render() {
    const {
      selectedNetworkId,
      selectedNetwork,
      selectedReport,
      selectedExtGamUnitIds,
      selectedRequestTypes,
      selectedDataview,
      selectedYieldSet,
      selectedYieldSetIds,
      inputExtGamUnitIds,
      ysExtGamUnitIds,
      isForceReload,
      startDate,
      endDate,
      tabList,
      selectedTab,

      errMsg,
      isLoading,
      data,
      builds,
      updateTime,
      tempParam,
      checkInputExtGamUnitIds,

      isDownloading,
      isPromptModalOpen,
      promptHeader,
      promptMessage,
      promptAction,
      promptMsg,

      isShowAdvanced,
    } = this.state;

    const report = _.get(this.props, "match.params.report");

    const isChanged =
      !data ||
      !_.isEqual(
        {
          ...tempParam,
          extGamUnitIds: null,
          yieldSetIds: null,
          queryIds:
            selectedTab === TABS.EXT_GAM_UNIT_ID
              ? tempParam.extGamUnitIds
              : tempParam.yieldSetIds,
        },
        {
          report,
          networkId: selectedNetworkId,
          startDate,
          endDate,
          reload: isForceReload,
          requestType: selectedRequestTypes,
          extGamUnitIds: null,
          yieldSetIds: null,
          paramType: selectedTab,
          queryIds:
            selectedTab === TABS.EXT_GAM_UNIT_ID
              ? selectedExtGamUnitIds
              : selectedYieldSetIds,
        }
      );

    const isGettingDataDisabled = !startDate || !endDate || !selectedNetworkId;

    const columns =
      data && data[selectedReport] ? data[selectedReport].columns : [];

    const parsedCol = columns.map((cKey) => ({
      key: cKey,
      label: _.startCase(cKey),
      ...(tableStyleMap.get(cKey) || {}),
    }));

    const records =
      data && data[selectedReport] ? data[selectedReport].records : [];

    const fileName = `${selectedNetworkId}_${report}_${selectedReport}_${startDate}_${endDate}.csv`;

    const { location } = this.props;
    const queryString = location.search;

    const reportList =
      selectedDataview === DATAVIEW.SOURCE
        ? _.keys(data)
        : _.values(PROCESSED_REPORTS[report]);

    let dataList = [];
    if (
      data &&
      selectedReport === PROCESSED_REPORTS[REPORT.CODE_SNIPPET].REPORT_5
    ) {
      dataList = [
        data[PROCESSED_REPORTS[REPORT.CODE_SNIPPET].REPORT_3],
        data[PROCESSED_REPORTS[REPORT.CODE_SNIPPET].REPORT_4],
        data[PROCESSED_REPORTS[REPORT.CODE_SNIPPET].REPORT_5],
      ];
    }

    if (
      data &&
      (selectedReport === PROCESSED_REPORTS[REPORT.AB_TESTING].REPORT_8 ||
        selectedReport === PROCESSED_REPORTS[REPORT.CODE_SNIPPET].REPORT_6)
    ) {
      dataList = [data[PROCESSED_REPORTS[REPORT.AB_TESTING].REPORT_1]];
    }
    return (
      <>
        <div className="border-bottom border flex bg-white">
          <div
            style={fixedWidthStyle}
            className="flex items-center justify-start gap-2 py-2"
          >
            <div className="mr-2 text-base font-bold">
              Integration Verification
            </div>
            <NavLink
              to={`${reportPaths.abTesting}${queryString}`}
              className={baseNavButton}
              activeClassName={selectedButton}
            >
              A/B Testing
            </NavLink>
            <NavLink
              to={`${reportPaths.codeSnippet}${queryString}`}
              className={baseNavButton}
              activeClassName={selectedButton}
            >
              Code Snippet
            </NavLink>
            {report === REPORT.CODE_SNIPPET && (
              <div
                className="ml-auto flex cursor-pointer items-center gap-2 text-sm underline hover:text-blue-700"
                onClick={() =>
                  window.open(
                    `${window.location.origin}/#/ops-mgmt/account/network/${selectedNetwork.networkId}/code-snippet`
                  )
                }
              >
                Code Snippet <FaExternalLinkAlt />
              </div>
            )}
          </div>
        </div>
        <div className="bg-white">
          <div className="sticky top-0 z-40 bg-gray-200 py-2">
            <div
              className="flex flex-col justify-between"
              style={fixedWidthStyle}
            >
              <div className="flex w-full items-center gap-2">
                <div className="w-1/3">
                  <NetworksSelector
                    selectedId={selectedNetworkId}
                    handleOnChange={this.handleNetworksChanged}
                  ></NetworksSelector>
                </div>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  dateRanges={[]}
                  months={1}
                  handleDateRangeChanged={this.handleDateRangeFilterChanged}
                ></DateRangePicker>
                <label className="rounded mr-2 flex cursor-pointer items-center py-2">
                  <Checkbox
                    isChecked={isForceReload}
                    onChange={(val) => this.setState({ isForceReload: val })}
                  ></Checkbox>
                  <span className="ml-1 align-middle text-sm font-semibold text-gray-600 hover:text-gray-700">
                    Force Reload
                  </span>
                </label>
                <button
                  type="button"
                  onClick={this.handleGetData}
                  className={`rounded ml-auto px-6 py-2 font-semibold text-white ${
                    isGettingDataDisabled ||
                    !isChanged ||
                    !checkInputExtGamUnitIds
                      ? "cursor-not-allowed bg-blue-200"
                      : "shadow bg-blue-400 hover:bg-blue-500"
                  }`}
                  disabled={
                    isGettingDataDisabled ||
                    !isChanged ||
                    !checkInputExtGamUnitIds
                  }
                >
                  Get data
                </button>
                <button
                  type="button"
                  className="rounded shadow bg-blue-400 px-6 py-2 font-semibold text-white hover:bg-blue-500"
                  onClick={this.handleDownloadData}
                >
                  Download all
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <SwitchTabs
                  tabs={tabList}
                  selectedTabKey={selectedTab}
                  handleChange={this.handleTabChange}
                />
                <div
                  className={`flex-auto ${
                    selectedTab === TABS.EXT_GAM_UNIT_ID ? "block" : "hidden"
                  }`}
                >
                  <input
                    type="text"
                    id="ext-gam-unit-ids-filter"
                    value={inputExtGamUnitIds}
                    className="rounded focus:outline-none hover:shadow w-full appearance-none border-2 border-gray-300 bg-white px-2 py-1 leading-normal hover:border-blue-300 focus:border-blue-400 focus:shadow-inner"
                    placeholder="Ext GAM Unit ID (use , to separate)"
                    onChange={this.handleExtGamUnitIdsChange}
                  ></input>
                </div>
                {selectedNetworkId && (
                  <div
                    className={`w-1/2 ${
                      selectedTab === TABS.YIELD_SET ? "block" : "hidden"
                    }`}
                  >
                    <YieldSetsMultiSelector
                      networkId={selectedNetworkId}
                      handleOnChange={this.handleYieldSetChange}
                      defaultValue={selectedYieldSetIds}
                      selectedYieldSet={selectedYieldSet}
                    />
                  </div>
                )}
                {selectedTab === TABS.YIELD_SET &&
                  ysExtGamUnitIds.length > 0 && (
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-blue-600">
                        {ysExtGamUnitIds.length}
                      </span>{" "}
                      Unit
                      {ysExtGamUnitIds.length > 1 ? "s" : ""} selected
                    </div>
                  )}

                <button
                  type="button"
                  className="focus:outline-none ml-auto flex-none p-2 text-blue-600 hover:text-blue-500"
                  onClick={() =>
                    this.setState({ isShowAdvanced: !isShowAdvanced })
                  }
                >
                  <div
                    className="flex items-center justify-end"
                    style={{ width: "8.5rem" }}
                  >
                    {isShowAdvanced ? (
                      <>
                        Hide advanced
                        <FiChevronUp></FiChevronUp>
                      </>
                    ) : (
                      <>
                        Show advanced
                        <FiChevronDown></FiChevronDown>
                      </>
                    )}
                  </div>
                </button>
              </div>

              {report === REPORT.AB_TESTING && (
                <div className="text-xs leading-tight text-gray-700">
                  "Prebid request report" is collected only if Ext GAM Unit ID
                  filter is provided.
                </div>
              )}

              {isShowAdvanced && (
                <div className="border-t mt-2 border-gray-300 pt-2">
                  <div className="flex w-full items-center gap-2 pl-1">
                    <div className="text-gray-800">
                      Request Type:{" "}
                      <span
                        className={`text-sm ${
                          selectedRequestTypes.length > 0
                            ? "text-gray-600"
                            : "font-semibold text-blue-600"
                        }`}
                      >
                        {selectedNetwork &&
                        (selectedNetwork.billableRequestTypes || []).length > 0
                          ? `Default (Billable Request Types - ${(
                              selectedNetwork.billableRequestTypes || []
                            ).join(", ")})`
                          : ""}
                      </span>
                    </div>
                  </div>
                  <RequestTypesSelector
                    isMulti={true}
                    selectedValue={selectedRequestTypes}
                    handleOnChange={(value) =>
                      this.setState({ selectedRequestTypes: value })
                    }
                  ></RequestTypesSelector>
                </div>
              )}
            </div>
          </div>
          <div className="min-h-screen bg-white py-4" style={fixedWidthStyle}>
            {errMsg && <div className="text-center text-red-600">{errMsg}</div>}
            {isLoading && (
              <div
                className="fixed left-0 top-0 z-50 flex h-screen w-screen items-center justify-center"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <LoadingUI iconOnly={true}></LoadingUI>
              </div>
            )}
            {data ? (
              <>
                <div className="mb-2 flex w-full items-center justify-start gap-2">
                  {_.values(DATAVIEW).map((dv) => (
                    <button
                      type="button"
                      className={
                        selectedDataview === dv ? selectedButton : baseButton
                      }
                      style={{ width: "calc(16.66% - (0.5rem * 5) / 6)" }}
                      key={dv}
                      onClick={() => this.handleDataViewChanged(dv)}
                    >
                      {dv}
                    </button>
                  ))}
                </div>
                <div
                  className={`rounded border mb-2 px-4 py-2 ${
                    selectedDataview === DATAVIEW.SOURCE
                      ? "bg-gray-100"
                      : "bg-orange-100"
                  }`}
                >
                  <div className="flex">
                    <div className="text-base text-gray-700">
                      <span className="font-semibold">
                        {_.startCase(tempParam.report)}
                      </span>{" "}
                      - {selectedDataview} (
                      <span className="font-semibold">
                        {_.keys(data).length}
                      </span>
                      )
                    </div>
                    <div className="mb-2 ml-auto text-right text-sm font-semibold text-gray-700">
                      Data last updated:{" "}
                      <DateTimeFormatter
                        datetime={updateTime}
                      ></DateTimeFormatter>
                    </div>
                  </div>
                  <div className="mb-2 flex flex-wrap gap-2">
                    {_.map(reportList, (val, i) => (
                      <button
                        className={`${
                          selectedReport === val ? selectedButton : baseButton
                        } text-sm`}
                        style={{ width: "calc(16.66% - (0.5rem * 5) / 6)" }}
                        key={i}
                        onClick={() => this.handleReportChanged(val)}
                      >
                        {_.startCase(val)}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedDataview === DATAVIEW.SOURCE ? (
                  <SourceDataView
                    records={records}
                    columns={parsedCol}
                    fileName={fileName}
                  ></SourceDataView>
                ) : (
                  <ProcessedDataView
                    records={records}
                    columns={columns}
                    dataList={dataList}
                    report={selectedReport}
                    builds={builds}
                  ></ProcessedDataView>
                )}

                {selectedDataview === DATAVIEW.PROCESSED && (
                  <ScrollToTop></ScrollToTop>
                )}
              </>
            ) : (
              !errMsg && (
                <div className="text-center text-gray-600">No data</div>
              )
            )}
          </div>
        </div>

        {isPromptModalOpen && (
          <PromptModal
            redo={false}
            msg={promptMsg}
            isLoading={isDownloading}
            isOpenModal={isPromptModalOpen}
            handleConfirm={promptAction}
            handleCancel={() => this.setState({ isPromptModalOpen: false })}
            header={promptHeader}
          >
            {promptMessage}
          </PromptModal>
        )}
      </>
    );
  }
}

class DownloadMessage extends React.Component {
  render() {
    const {
      networkId,
      networkName,
      startDate,
      endDate,
      isForceReload,
      selectedTab,
      extGamUnitIds,
      selectedYieldSet,
      selectedRequestTypes,
    } = this.props;

    return (
      <>
        <div>
          Network:{" "}
          <span className="font-semibold text-blue-600">
            {networkId} {networkName}
          </span>
        </div>
        <div>
          Date Range:{" "}
          <span className="font-semibold text-blue-600">
            {startDate} ~ {endDate}
          </span>
        </div>
        <div>
          Force Reload:{" "}
          <span className="font-semibold text-blue-600">
            {isForceReload ? "Yes" : "No"}
          </span>
        </div>
        {selectedTab === TABS.EXT_GAM_UNIT_ID ? (
          <div>
            External GAM Unit:{" "}
            <span className="font-semibold text-blue-600">
              {extGamUnitIds.length === 0 ? "All" : extGamUnitIds.join(", ")}
            </span>
          </div>
        ) : (
          <div className="flex gap-1">
            <div>Yield Set: </div>
            <div>
              <div className="font-semibold text-blue-600">
                {(selectedYieldSet || []).length === 0
                  ? "All"
                  : selectedYieldSet
                      .map(({ yieldSetId, name }) => `${yieldSetId} - ${name}`)
                      .join(", ")}
              </div>
              {extGamUnitIds && (
                <div className="font-semibold text-blue-600">
                  {extGamUnitIds.length} units selected
                </div>
              )}
            </div>
          </div>
        )}
        <div>
          Request Type:{" "}
          <span className="font-semibold text-blue-600">
            {selectedRequestTypes.length === 0
              ? "Default (Billable Request Types)"
              : selectedRequestTypes.join(", ")}
          </span>
        </div>
      </>
    );
  }
}

export default IntegrationVerificationViewer;
